<template>
  <div class="container">
    <transition name="template-overlay-fade">
      <div v-if="showCreateCourseTemplate" class="template-overlay">
        <div class="create-template-modal">
          <button class="create-template-close" type="button" aria-label="Close" @click="closeCreateCourseTemplate">CANCEL</button>
          <CreateCourseTemplate
            :professor-id="professorId"
            :success-cb="handleCourseTemplateCreated"
          ></CreateCourseTemplate>
        </div>
      </div>
    </transition>
    <div class="main-box">
      <div class="new-semester-form-container">
        <md-steppers md-linear :md-active-step.sync="activeStep" class="new-semester-stepper">
          <md-step id="semester-basics" md-label="Semester Basics" :md-done.sync="stepDone.basics" class="stepper-input-container">
            <div class="step-piece">
              <h3 class="label">Semester Name</h3>
              <md-field> 
                  <label class="label">Semester Name</label>
                  <md-input v-model="newSemesterName"></md-input>
                  <span v-if="inputError === 'name'" class="error form-error">Please enter a semester name</span>
              </md-field>
            </div>
            <div class="step-piece">
                <h3 class="label">Start Date</h3>
              <md-datepicker v-model="newSemesterStart" :md-disabled-dates="isDateDisabled" md-immediately/>
            </div>
            <div class="step-piece">
                <div class="flow-mode-label-row">
                  <h3 class="label">Use Flow Mode</h3>
                  <button
                    type="button"
                    class="flow-mode-help-btn"
                    aria-label="Flow mode help"
                    @click="showFlowModeHelp = !showFlowModeHelp"
                  >
                    <i class="fas fa-question-circle"></i>
                  </button>
                </div>
                <div class="radio-options">
                  <md-radio v-model="newSemesterUseFlowMode" :value="true" class="md-primary">Yes</md-radio>
                  <md-radio v-model="newSemesterUseFlowMode" :value="false" class="md-primary">No</md-radio>
                </div>
                <div v-if="showFlowModeHelp" class="flow-mode-help-text"><p>Use flow mode if you want the game to move forward as soon as each team finishes performing their required actions. Otherwise, the game moves on a scheduled basis (Wednesday @midnight for elections -- grading of financial statements will happen the following sunday @midnight).</p></div>
            </div>
            <div class="step-actions">
              <md-button class="md-raised btn-primary" @click="goToCourseStep">Next</md-button>
            </div>
          </md-step>
          <md-step id="course-template" md-label="Course Template" :md-done.sync="stepDone.course" class="stepper-input-container">
            <div class="step-piece">
              <md-field>
                <label for="course">Select a Course Template</label>
                <md-select v-bind:class="{ 'select-course-error':inputError === 'course'}" v-model="selectedCourseTemplateId" name="dropdown">
                  <md-optgroup label="Public Course Templates">
                    <md-option v-for="publicCourseTemplate in publicCourseTemplates" v-bind:key="publicCourseTemplate.id" :value="publicCourseTemplate.id">{{publicCourseTemplate.name}}</md-option>
                  </md-optgroup>
                  <md-optgroup label="My Course Templates">
                    <md-option v-for="myCourseTemplate in myCourseTemplates" v-bind:key="myCourseTemplate.id" :value="myCourseTemplate.id">{{myCourseTemplate.name}}</md-option>
                  </md-optgroup>
                </md-select>
              </md-field> 
              <p v-if="selectedCourseTemplate && selectedCourseTemplate.description" class="course-template-description">
                <span style="font-size: 18px;">➜</span> {{ selectedCourseTemplate.description }}
              </p>
            </div>
            <div v-if="inputError === 'course'" class="error">Please select a course</div>
            <div class="step-piece">
              <h4>OR</h4>
            </div>
            <div class="step-piece">
              <md-button class="md-raised" @click="showCreateCourseTemplate = !showCreateCourseTemplate">
                design a course (advanced)
              </md-button>
            </div>
            <div class="step-actions">
              <md-button class="md-raised" @click="activeStep = 'semester-basics'">Back</md-button>
              <md-button class="md-raised btn-primary" @click="goToTeamStep">Next</md-button>
            </div>
          </md-step>
          <md-step id="team-setup" md-label="Team Setup" :md-done.sync="stepDone.teams" class="stepper-input-container">
            <div class="md-layout-item">             
              <md-field>
                <label>Number of Teams <span v-if="selectedCourseTemplate">(min {{minNumTeams}} max {{maxNumTeams}})</span></label>
                <md-input v-model="newSemesterNumTeams" type="number" :min="minNumTeams" :max="maxNumTeams" @change="onNumTeamsChange"></md-input>
                <span v-if="inputError === 'numTeams'" class="error form-error">Please enter the number of teams</span>
                <span v-if="inputError === 'minMaxNumTeams'" class="error form-error">The value must be within the range of {{minNumTeams}} and {{maxNumTeams}}</span>
              </md-field>              
            </div>
            <div class="md-layout-item">              
              <md-field>
                <label>Students per Team <span v-if="selectedCourseTemplate">(min {{minStudentsPerTeam}} max {{maxStudentsPerTeam}})</span></label>
                <md-input v-model="newSemesterNumStudentsPerTeam" type="number" :min="minStudentsPerTeam" :max="maxStudentsPerTeam"></md-input>
                <span v-if="inputError === 'numStudentsPerTeam'" class="error form-error">Please enter the number of students per team</span>
                <span v-if="inputError === 'minMaxNumStudentsPerTeam'" class="error form-error">The value must be with the range of {{minStudentsPerTeam}} and {{maxStudentsPerTeam}}</span>
              </md-field>              
            </div>
            <div class="step-piece" v-if="Number(newSemesterNumTeams) > 0">
              <div class="customize-teams-toggle">
                <md-checkbox v-model="useCustomTeams">Customize team names, tickers, and colors</md-checkbox>
              </div>
            </div>
            <div v-if="useCustomTeams && customTeams.length > 0" class="custom-teams-container">
              <div class="custom-team-row custom-team-header">
                <span class="custom-team-col-num">#</span>
                <span class="custom-team-col-name">Team Name</span>
                <span class="custom-team-col-ticker">Ticker</span>
                <span class="custom-team-col-color">Color</span>
                <span class="custom-team-col-preview">Preview</span>
              </div>
              <div v-for="(team, index) in customTeams" :key="index" class="custom-team-row">
                <span class="custom-team-col-num">{{ index + 1 }}</span>
                <md-field class="custom-team-col-name">
                  <md-input v-model="team.name" placeholder="e.g. Lions"></md-input>
                </md-field>
                <md-field class="custom-team-col-ticker">
                  <md-input v-model="team.stockSymbol" placeholder="e.g. LION" maxlength="5" @input="team.stockSymbol = team.stockSymbol.toUpperCase()"></md-input>
                </md-field>
                <div class="custom-team-col-color">
                  <input type="color" v-model="team.colorHex" class="color-picker-input" />
                </div>
                <div class="custom-team-col-preview">
                  <span class="team-preview-badge" :style="{ backgroundColor: team.colorHex, color: getContrastColor(team.colorHex) }">
                    {{ team.stockSymbol || '...' }}
                  </span>
                </div>
              </div>
              <span v-if="inputError === 'customTeams'" class="error">All team names, tickers, and colors must be filled in. Tickers must be unique.</span>
            </div>
            <div class="step-actions">
              <md-button class="md-raised" @click="activeStep = 'course-template'">Back</md-button>
              <md-button class="md-raised btn-primary" @click="goToReviewStep">Next</md-button>
            </div>
          </md-step>
          <md-step id="review-submit" md-label="Review & Submit" class="stepper-input-container">
            <div class="review-grid">
              <div class="review-row"><strong>Name:</strong> {{newSemesterName}}</div>
              <div class="review-row"><strong>Course Template:</strong> {{selectedCourseTemplate ? selectedCourseTemplate.name : ''}}</div>
              <div class="review-row"><strong>Start Date:</strong> {{newSemesterStart ? newSemesterStart.toDateString() : ''}}</div>
              <div class="review-row"><strong>Flow Mode:</strong> {{newSemesterUseFlowMode ? 'Yes' : 'No'}}</div>
              <div class="review-row"><strong>Number of Teams:</strong> {{newSemesterNumTeams}}</div>
              <div class="review-row"><strong>Students per Team:</strong> {{newSemesterNumStudentsPerTeam}}</div>
              <div v-if="useCustomTeams" class="review-row">
                <strong>Custom Teams:</strong>
                <div class="review-teams-list">
                  <span v-for="(team, i) in customTeams" :key="i" class="review-team-badge" :style="{ backgroundColor: team.colorHex, color: getContrastColor(team.colorHex) }">
                    {{ team.name }} ({{ team.stockSymbol }})
                  </span>
                </div>
              </div>
              <div v-else class="review-row"><strong>Teams:</strong> Randomly generated</div>
              <div class="review-row" v-if="!newSemesterUseFlowMode"><strong>Elections Open:</strong> Monday, {{ newSemesterStart ? newSemesterStart.toDateString() : '' }} at noon 12:00pm</div>
              <div class="review-row" v-if="!newSemesterUseFlowMode"><strong>Elections Due:</strong> The consecutive Wednesday by midnight 11:59pm</div>
              <div class="review-row" v-if="!newSemesterUseFlowMode"><strong>Financials Deadline:</strong> The consecutive Sunday by midnight 11:59pm</div>
            </div>
            <div class="step-actions">
              <md-button class="md-raised" @click="activeStep = 'team-setup'">Back</md-button>
              <md-button :disabled="httpWait" @click="submitNewSemester()" class="md-raised btn-primary" :class="{ 'btn-disabled' : httpWait }">
                Submit
                <md-progress-spinner v-if="httpWait" class="btn-spin" :md-diameter="20" :md-stroke="3" md-mode="indeterminate"></md-progress-spinner>
              </md-button>
            </div>
            <span class="error">{{serverError}}</span>
          </md-step>
        </md-steppers>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { MdField } from 'vue-material/dist/components'
import { MdButton } from 'vue-material/dist/components'
import { MdDatepicker } from 'vue-material/dist/components'
import { MdDialog } from 'vue-material/dist/components'
import { MdMenu } from 'vue-material/dist/components'
import { MdList } from 'vue-material/dist/components'
import { MdRadio } from 'vue-material/dist/components'
import { MdCheckbox } from 'vue-material/dist/components'
import { MdSteppers } from 'vue-material/dist/components'
import { MdSubheader } from 'vue-material/dist/components'
import { mapActions } from 'vuex'
import { DateTime } from "luxon";
import CreateCourseTemplate from './CreateCourseTemplate'

Vue.use(MdField);
Vue.use(MdButton);
Vue.use(MdDatepicker);
Vue.use(MdDialog);
Vue.use(MdMenu);
Vue.use(MdList);
Vue.use(MdRadio);
Vue.use(MdCheckbox);
Vue.use(MdSteppers);
Vue.use(MdSubheader);

export default {
  name: 'NewSemester',
  components: {
    CreateCourseTemplate
  },
  data() {
    let startDate = DateTime.local().set({weekday: 1}).startOf('day');
    const now = DateTime.local();
    if(startDate < now) {
      startDate = startDate.plus({days: 7});
    }
    return {
      newSemesterName: "",
      selectedCourseTemplate: null,
      newSemesterYear: "",
      newSemesterStart: startDate.toJSDate(),
      newSemesterUseFlowMode: false,
      newSemesterNumTeams: "",
      newSemesterNumStudentsPerTeam: "",
      minNumTeams: 0,
      maxNumTeams: 100,
      minStudentsPerTeam: 0,
      maxStudentsPerTeam: 100,
      serverError: "",
      inputError: "",
      httpWait: false,
      courseTemplates: [],
      publicCourseTemplates: [],
      myCourseTemplates: [],
      selectedCourseTemplateId: null,
      showCreateCourseTemplate: false,
      showFlowModeHelp: false,
      useCustomTeams: false,
      customTeams: [],
      activeStep: 'semester-basics',
      stepDone: {
        basics: false,
        course: false,
        teams: false
      }
    }
  },
  props: {
    showCreateSemester: Boolean,
    alertMessage: String,
    professorId: String
  },
  watch: {
    newSemesterUseFlowMode: function(val) {
      const today = DateTime.local().startOf('day');
      const selected = DateTime.fromJSDate(this.newSemesterStart).startOf('day');

      if(val) {
        if(selected < today) {
          this.newSemesterStart = today.toJSDate();
        }
        return;
      }

      if(selected.weekday !== 1 || selected < today) {
        let monday = today.set({weekday: 1}).startOf('day');
        if(monday < today) {
          monday = monday.plus({days: 7});
        }
        this.newSemesterStart = monday.toJSDate();
      }
    },
    selectedCourseTemplateId: function(val) {
      if(val) {
        this.selectedCourseTemplate = this.courseTemplates.find(courseTemplate => courseTemplate.id === val);
      }
    },
    selectedCourseTemplate: function(val) {
      if(val) {
        this.minNumTeams = val.minTeams;
        this.maxNumTeams = val.maxTeams;
        console.log("min students per team", val.minStudentsPerTeam);
        console.log("max students per team", val.maxStudentsPerTeam);
        console.log("min teams", val.minTeams);
        console.log("max teams", val.maxTeams);
        this.minStudentsPerTeam = val.minStudentsPerTeam;
        this.maxStudentsPerTeam = val.maxStudentsPerTeam;
      }
    },
    useCustomTeams: function(val) {
      if(val) {
        this.syncCustomTeams();
      }
    }
  },
  methods: {
    ...mapActions([
      'addSemester', 
      'fetchCourseList',
      'fetchCourseTemplates'
    ]),
    async refreshCourseTemplates() {
      this.courseTemplates = await this.fetchCourseTemplates({
        professorId: this.professorId
      });
      this.publicCourseTemplates = this.courseTemplates.filter(template => template.isSystemTemplate);
      this.myCourseTemplates = this.courseTemplates.filter(template => !template.isSystemTemplate);
    },
    isDateDisabled(date) {
      const selectedDate = DateTime.fromMillis(date.getTime()).startOf('day');
      const today = DateTime.local().startOf('day');

      if(this.newSemesterUseFlowMode) {
        return selectedDate < today;
      }

      const day = date.getDay();
      return day !== 1 || selectedDate < today;
    },
    async handleCourseTemplateCreated(createdTemplate) {
      await this.refreshCourseTemplates();
      if(createdTemplate && createdTemplate.id) {
        this.selectedCourseTemplateId = createdTemplate.id;
      }
      this.showCreateCourseTemplate = false;
    },
    closeCreateCourseTemplate() {
      this.showCreateCourseTemplate = false;
    },
    onNumTeamsChange() {
      if(this.useCustomTeams) {
        this.syncCustomTeams();
      }
    },
    syncCustomTeams() {
      const num = Number(this.newSemesterNumTeams) || 0;
      const defaultColors = ['#FF0000','#FF8000','#FFFF00','#80FF00','#00FFFF','#0080FF','#0000FF','#8000FF','#FF00FF','#FF0080','#000000','#808080'];
      while(this.customTeams.length < num) {
        const idx = this.customTeams.length;
        this.customTeams.push({ name: '', stockSymbol: '', colorHex: defaultColors[idx] || '#' + Math.floor(Math.random()*16777215).toString(16).padStart(6, '0') });
      }
      if(this.customTeams.length > num) {
        this.customTeams.splice(num);
      }
    },
    getContrastColor(hex) {
      if(!hex || hex.length < 7) return '#000000';
      const r = parseInt(hex.substr(1, 2), 16);
      const g = parseInt(hex.substr(3, 2), 16);
      const b = parseInt(hex.substr(5, 2), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#000000' : '#FFFFFF';
    },
    async submitNewSemester() {
      this.httpWait = true;
      this.newSemesterYear = this.newSemesterStart.getFullYear();
      if(this.validateForm()) {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
        try {
          const semesterPayload = {
            name: this.newSemesterName,
            year: this.newSemesterYear,
            season: this.newSemesterSeason,
            startTime: (this.newSemesterStart.getTime() / 1000),
            useFlowMode: this.newSemesterUseFlowMode,
            timezone,
            numTeams: parseInt(this.newSemesterNumTeams),
            numStudentsPerTeam: parseInt(this.newSemesterNumStudentsPerTeam),
            courseTemplateId: this.selectedCourseTemplate.id,
            randomizeDemand: !!(this.selectedCourseTemplate && this.selectedCourseTemplate.randomizeDemand)
          };
          if(this.useCustomTeams && this.customTeams.length > 0) {
            semesterPayload.customTeams = this.customTeams.map(t => ({
              name: t.name,
              stockSymbol: t.stockSymbol,
              colorHex: t.colorHex
            }));
          }
          await this.addSemester(semesterPayload);
          this.$emit("update:dismissAlert", false);
          this.$emit("update:showFormSuccess", true);
          this.$emit("update:showCreateSemester", false);
          this.$emit("update:alert-message", "Successfully created a game!");
          this.httpWait = false;
        } catch(err) {
          this.serverError = "Something went wrong. Please try again";
          this.httpWait = false;
          return err;
        }
      }
      this.httpWait = false;
    },
    goToCourseStep() {
      if(this.validateSemesterBasics()) {
        this.stepDone.basics = true;
        this.activeStep = 'course-template';
      }
    },
    goToTeamStep() {
      if(this.validateCourseStep()) {
        this.stepDone.course = true;
        this.activeStep = 'team-setup';
      }
    },
    goToReviewStep() {
      if(this.validateTeamStep()) {
        this.stepDone.teams = true;
        this.activeStep = 'review-submit';
      }
    },
    validateSemesterBasics() {
      if(!this.newSemesterName) {
        this.inputError = "name";
        return false;
      }

      this.inputError = "";
      return true;
    },
    validateCourseStep() {
      if(!this.selectedCourseTemplate) {
        this.inputError = "course";
        return false;
      }

      this.inputError = "";
      return true;
    },
    validateTeamStep() {
      const numTeams = Number(this.newSemesterNumTeams);
      const numStudentsPerTeam = Number(this.newSemesterNumStudentsPerTeam);

      if(!this.newSemesterNumTeams) {
        this.inputError = "numTeams";
        return false;
      }

      if(!this.newSemesterNumStudentsPerTeam) {
        this.inputError = "numStudentsPerTeam";
        return false;
      }

      if(numTeams < this.minNumTeams || numTeams > this.maxNumTeams) {
        this.inputError = "minMaxNumTeams";
        return false;
      }

      if(numStudentsPerTeam < this.minStudentsPerTeam || numStudentsPerTeam > this.maxStudentsPerTeam) {
        this.inputError = "minMaxNumStudentsPerTeam";
        return false;
      }

      if(this.useCustomTeams) {
        const tickers = new Set();
        for(const team of this.customTeams) {
          if(!team.name || !team.stockSymbol || !team.colorHex) {
            this.inputError = "customTeams";
            return false;
          }
          if(tickers.has(team.stockSymbol)) {
            this.inputError = "customTeams";
            return false;
          }
          tickers.add(team.stockSymbol);
        }
      }

      this.inputError = "";
      return true;
    },
    validateForm() {
      if(!this.validateSemesterBasics()) {
        this.activeStep = 'semester-basics';
        return false;
      }

      if(!this.validateCourseStep()) {
        this.activeStep = 'course-template';
        return false;
      }

      if(!this.validateTeamStep()) {
        this.activeStep = 'team-setup';
        return false;
      }

      return true;
    }
  },
  async created() {
    this.fetchCourseList();
    this.useFlowMode = !!localStorage.getItem('accountonomics.flowMode');
    await this.refreshCourseTemplates();
  }
}
</script>

<style scoped>

  ::v-deep .md-datepicker .md-icon::after {
    content: none !important;
    display: none !important;
  }

  ::v-deep .md-datepicker .md-icon,
  ::v-deep .md-datepicker .md-icon svg,
  ::v-deep .md-datepicker .md-icon i {
    color: var(--text-primary) !important;
    fill: var(--text-primary) !important;
  }

  .new-semester-stepper 
  {
    width: 100%;
    height: 100%;
  }

  .stepper-input-container {
    /* min-width: 100%; */
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background-color: var(--main-bg-color) !important;
    color: var(--text-primary) !important;
    max-height: 100%;
  }

  .step-piece {
    margin: 1em;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  h1 {
    padding: 1em;
  }
  .md-layout-item {
    min-width: 350px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  .container {
    margin: 1em;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    max-height: 80vh;
    position: relative;
  }
  .template-overlay {
    position: fixed;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 1em;
    background: rgba(0, 0, 0, 0.8);
    z-index: 1000;
    overflow-y: auto;
  }
  .create-template-modal {
    position: relative;
    width: min(1200px, 100%);
  }
  
  .create-template-close {
    height: 4em;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background-color: var(--main-bg-color);
    color: var(--text-primary);
    cursor: pointer;
    font-weight: 700;
    line-height: 1;
    padding: 14px;
  }
  .create-template-close:hover {
    border-color: var(--btn-primary-color);
  }
  .template-overlay-fade-enter-active,
  .template-overlay-fade-leave-active {
    transition: opacity 0.2s ease;
  }
  .template-overlay-fade-enter,
  .template-overlay-fade-leave-to {
    opacity: 0;
  }
  .main-box {
    background-color: var(--main-bg-color) !important;
    /* border: 2px solid var(--text-primary); */
    max-height: 600px;
  }
  .text-center {
    text-align: center;
    color: var(--text-primary);
  }
  .btn-group {
    padding-bottom: 1em;
  }
  .new-semester-form-container {
    padding: 1em;
    height: 100%;
  }
  .md-card {
    width: 320px;
    margin: 4px;
    display: inline-block;
    vertical-align: top;
  }
  .highlightSelected {
    background-color: #448aff;
  }
  .error {
    color: #ff1744;
  }
  .form-error {
    display: block!important;
    left: 0;
    height: 20px;
    position: absolute;
    bottom: -22px;
    font-size: 12px;
    transition: .3s cubic-bezier(.4,0,.2,1);
  }
  .label {
    text-align: left;
  }
  .select-course-error {
    border-color: var(--warning-text-color);
  }
  .course-template-description {
    display: block;
    margin-top: 0.5em;
    color: var(--text-primary);
    text-align: left;
    line-height: 1.35;
    width: 100%;
  }
  .radio-options {
    text-align: left;
    background-color: var(--inner-box-color);
    padding: 8px;
    border: 2px solid var(--text-primary);
    border-radius: 2px;
  }
  .flow-mode-label-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .flow-mode-help-btn {
    border: none;
    background: transparent;
    color: var(--text-primary);
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }
  .flow-mode-help-text {
    margin-top: 8px;
    color: var(--text-primary);
    text-align: left;
    max-width: 500px;
  }
  .step-actions {
    margin-top: 1em;
    display: flex;
    justify-content: space-between;
    gap: 0.5em;
  }
  .or-create-row {
    margin: 0.5em 0 1em;
    text-align: left;
  }
  .review-grid {
    text-align: left;
  }
  .review-row {
    margin-bottom: 0.5em;
    color: var(--text-primary);
  }
  .customize-teams-toggle {
    width: 100%;
    text-align: left;
  }
  .custom-teams-container {
    width: 100%;
    max-height: 300px;
    overflow-y: auto;
    padding: 0 1em;
  }
  .custom-team-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }
  .custom-team-header {
    font-weight: bold;
    color: var(--text-primary);
    margin-bottom: 8px;
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 4px;
  }
  .custom-team-col-num {
    width: 30px;
    text-align: center;
    color: var(--text-primary);
  }
  .custom-team-col-name {
    flex: 2;
  }
  .custom-team-col-ticker {
    flex: 1;
  }
  .custom-team-col-color {
    width: 50px;
    display: flex;
    justify-content: center;
  }
  .custom-team-col-preview {
    width: 70px;
    display: flex;
    justify-content: center;
  }
  .color-picker-input {
    width: 40px;
    height: 34px;
    border: none;
    padding: 0;
    cursor: pointer;
    background: transparent;
  }
  .team-preview-badge {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: bold;
    white-space: nowrap;
  }
  .review-teams-list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
  }
  .review-team-badge {
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 13px;
    font-weight: bold;
  }
</style>
